import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lightTheme, darkTheme } from '@next-qr/mini-app-sdk';
import type { AppNavigate, Theme } from '@next-qr/mini-app-sdk';
import { MiniAppSandbox } from '../components/MiniAppSandbox';

const THEME_PREF_KEY = 'mini-app-color-scheme';

type ColorSchemePref = 'light' | 'dark' | 'auto';

function resolveTheme(pref: ColorSchemePref): Theme {
  if (pref === 'dark') return darkTheme;
  if (pref === 'light') return lightTheme;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? darkTheme : lightTheme;
}

function useHostTheme(): [Theme, (pref: ColorSchemePref) => void] {
  const [pref, setPref] = useState<ColorSchemePref>(
    () => (localStorage.getItem(THEME_PREF_KEY) as ColorSchemePref | null) ?? 'auto',
  );
  const [theme, setTheme] = useState<Theme>(() => resolveTheme(pref));

  // Listen for system changes when preference is 'auto'
  useEffect(() => {
    if (pref !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setTheme(e.matches ? darkTheme : lightTheme);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [pref]);

  const setPreference = useCallback((next: ColorSchemePref) => {
    setPref(next);
    setTheme(resolveTheme(next));
    if (next === 'auto') {
      localStorage.removeItem(THEME_PREF_KEY);
    } else {
      localStorage.setItem(THEME_PREF_KEY, next);
    }
  }, []);

  return [theme, setPreference];
}

export function MiniAppPage() {
  const { appId, '*': splat } = useParams<{ appId: string; '*': string }>();
  const navigate = useNavigate();
  const [theme] = useHostTheme();

  const appNavigate = useMemo<AppNavigate>(() => ({
    toHost: (screen: string) => navigate(screen === 'Home' ? '/' : `/${screen.toLowerCase()}`),
    toMiniApp: (id: string) => navigate(`/app/${id}`),
    goBack: () => navigate(-1 as any),
  }), [navigate]);

  const initialPath = splat ? `/${splat}` : '/';
  const onNavigate = useCallback((subPath: string) => {
    navigate(`/app/${appId}${subPath}`, { replace: true });
  }, [navigate, appId]);

  return (
    <MiniAppSandbox
      appId={appId!}
      theme={theme}
      navigate={appNavigate}
      initialPath={initialPath}
      onNavigate={onNavigate}
    />
  );
}
